import mixpanel from "mixpanel-browser"
import { useEffect } from "react"
import { matchPath, useLocation } from "react-router"
import { useGetAsset } from "../components/WatchedLocations/hooks/useAssetService"
import { useOrganizationId } from "../hooks/useOrganizationId"
import { useSearchParams } from "../hooks/useSearchParams"
import { ROUTES } from "./constants"
import { AUTH0_CLIENT_ID } from "./env"
import { getLocationId, hashData } from "./helpers"

import useAuth from "../utils/auth"

interface AnalyticsNotificationSettings {
  analysisEnabled: boolean
  alertsEnabled: boolean
  /** ```ts
   * AssetNotificationAlertLevel[alert_level]
   * ```
   */
  alert_level: string
  /** ```ts
   * Disease_Type[threat]
   * ```
   */
}

type ManageLocationAction =
  | {
      action: "add" | "edit"
      locationId: string | undefined
      /**```ts
       * IndustryType[industry]
       * ```*/
      industry: string
    }
  | { action: "delete"; locationId: string | undefined }

interface AnalyticsEvents {
  // click events
  CLICK_SAVE_PDF: null
  SAVE_NOTIFICATIONS: AnalyticsNotificationSettings
  DISCARD_NOTIFICATIONS: null
  OPEN_DISEASE_FACTOR: {
    /**```ts
     * Disease_Type[disease]
     * ```*/
    disease: string
    location_id: string | undefined
  }
  CLICK_ALERT_SOURCE: {
    title: string
    link: string
  }
  CLICK_ALERT_SEE_MORE: {
    title: string
    link: string
  }
  WORKFORCE_PLANNING_INDUSTRY_CHANGE: {
    /**```ts
     * IndustryType[industry]
     * ```*/
    industry: string
  }
  MANAGE_LOCATION: ManageLocationAction
  OPEN_ASK: null
  QUERY_ASK: {
    query: string[]
  }
  CLICK_ASK_HISTORY: {
    query: string[]
  }
  CLICK_ASK_COPY: {
    query: string[]
  }
  CREATE_USER_FEEDBACK: {
    queryId: string
    responseIsHelpful: boolean
    reason?: string
    additionalFeedback?: string
  }
  OPEN_ASK_BIBLIOGRAPHY: {
    query: string[]
  }
}

const apiKey = import.meta.env.MIXPANEL_TOKEN || "empty token"

mixpanel.init(apiKey, {
  persistence: "localStorage",
  // debug: true,
})

export const useMixpanel = () => {
  const orgId = useOrganizationId()
  const { user } = useAuth()
  useMixpanelPageView()
  const user_id = user?.email
  useEffect(() => {
    const initMixpanel = async () => {
      const hashedUserId = user_id
        ? await hashData(AUTH0_CLIENT_ID + user_id)
        : undefined
      const hashedOrgId = orgId.data
        ? await hashData(AUTH0_CLIENT_ID + orgId.data)
        : undefined
      hashedUserId && mixpanel.identify(hashedUserId)
      hashedOrgId && mixpanel.set_group("orgId", hashedOrgId)
    }
    initMixpanel().catch(console.error)
  }, [orgId.data, user_id])
}

export const useMixpanelPageView = () => {
  const { pathname, search } = useLocation()
  const searchParams = useSearchParams()
  const { isLoading } = useAuth()
  const assetMatch = matchPath(ROUTES.ASSET, pathname)
  const asset_id: string | undefined =
    assetMatch?.params.asset_id ?? searchParams.params.asset_id
  const { data: assetData, isLoading: assetLoading } = useGetAsset(asset_id)

  const assetLocationId = getLocationId(assetData?.asset)
  const location_id: string | undefined =
    assetLocationId ?? searchParams.params.id
  // needed for the useEffect to trigger on url and query param changes
  const url = pathname + search
  // trying to reduce deps so we don't trigger the effect too much
  const loading = isLoading || assetLoading
  useEffect(() => {
    if (loading || !url) return

    // handle registering location_id for all events on page
    if (!location_id) {
      mixpanel.unregister("location_id", {
        persistent: false,
      })
    } else {
      mixpanel.register(
        {
          location_id: location_id,
        },
        {
          persistent: false,
        }
      )
    }

    // track pageview after registering location_id
    mixpanel.track_pageview()

    return () => {
      // clean up registered properties
      mixpanel.unregister("location_id", {
        persistent: false,
      })
    }
  }, [loading, location_id, url])
}

export const trackEvent = <Event extends keyof AnalyticsEvents>(
  event: Event,
  properties?: AnalyticsEvents[Event]
) => {
  mixpanel.track(event, { ...properties })
}

const getByteLength = (str: string) => new Blob([str]).size
/**
 * Splits a string into chunks small enough
 * for mixpanel custom properties
 *
 * https://docs.mixpanel.com/docs/data-structure/property-reference/data-type#string
 */
export const splitIntoChunks = (str: string, chunkSize = 255) => {
  const chunks = []
  let currentChunk = ""
  let currentChunkSize = 0

  for (const char of str) {
    const charSize = getByteLength(char)
    if (currentChunkSize + charSize > chunkSize) {
      chunks.push(currentChunk)
      currentChunk = char
      currentChunkSize = charSize
    } else {
      currentChunk += char
      currentChunkSize += charSize
    }
  }

  if (currentChunk) {
    chunks.push(currentChunk)
  }

  return chunks
}
